'use client';

import React, { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import {
  LayoutDashboard,
  Upload,
  Zap,
  RefreshCw,
  Activity,
  Settings,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMondayAuth } from '@/contexts/MondayAuthContext';
import { SidebarNavItem } from './SidebarNavItem';
import { WorkspaceSelector } from './WorkspaceSelector';
import { UpgradeCTA } from './UpgradeCTA';
import { Separator } from '@/components/ui/separator';

export interface SidebarProps {
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

export function Sidebar({ collapsed: controlledCollapsed, onCollapsedChange }: SidebarProps) {
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const { organization } = useMondayAuth();
  const pathname = usePathname();

  // Use controlled or internal state
  const collapsed = controlledCollapsed ?? internalCollapsed;
  const setCollapsed = onCollapsedChange ?? setInternalCollapsed;

  // Badge counts for nav items
  const [badges, setBadges] = useState<{
    activeOperations: number;
    availableRollbacks: number;
  }>({ activeOperations: 0, availableRollbacks: 0 });

  // Load badge counts
  useEffect(() => {
    if (!organization) return;

    const loadBadges = async () => {
      try {
        const response = await fetch(
          `/api/dashboard/badges?organizationId=${organization.id}`
        );
        if (response.ok) {
          const data = await response.json();
          setBadges(data.badges);
        }
      } catch (err) {
        console.error('Failed to load badge counts:', err);
      }
    };

    loadBadges();
    // Refresh badges every 30 seconds
    const interval = setInterval(loadBadges, 30000);
    return () => clearInterval(interval);
  }, [organization]);

  const mainNavItems = [
    {
      href: '/',
      icon: <LayoutDashboard className="w-5 h-5" />,
      label: 'Dashboard',
    },
    {
      href: '/csv-import',
      icon: <Upload className="w-5 h-5" />,
      label: 'CSV Import',
      badge: organization?.subscription_tier === 'free' ? 'Free' : undefined,
      badgeVariant: 'outline' as const,
    },
    {
      href: '/bulk-operations',
      icon: <Zap className="w-5 h-5" />,
      label: 'Bulk Operations',
    },
    {
      href: '/recurring-patterns',
      icon: <RefreshCw className="w-5 h-5" />,
      label: 'Recurring Tasks',
    },
    {
      href: '/activity',
      icon: <Activity className="w-5 h-5" />,
      label: 'Activity',
      badge: badges.activeOperations > 0 ? badges.activeOperations : undefined,
      badgeVariant: 'secondary' as const,
    },
  ];

  return (
    <aside
      className={cn(
        'relative flex flex-col bg-[var(--surface-sidebar)] border-r border-[var(--border-default)] transition-all duration-300 ease-in-out',
        collapsed ? 'w-[60px]' : 'w-64'
      )}
    >
      {/* Header: Logo + Wordmark */}
      <div className={cn(
        'flex items-center h-16 px-4 border-b border-[var(--border-default)]',
        collapsed && 'justify-center px-2'
      )}>
        {/* Logo Icon */}
        <img src="/taskloops-logo.png" alt="TaskLoops" className="flex-shrink-0 w-8 h-8 rounded-lg" />

        {/* Wordmark */}
        {!collapsed && (
          <div className="ml-3">
            <h1 className="text-lg font-bold font-heading text-[var(--text-heading)]">TaskLoops</h1>
            <p className="text-xs text-[var(--text-secondary)]">for monday.com</p>
          </div>
        )}
      </div>

      {/* Workspace Selector */}
      <div className={cn('px-3 py-3', collapsed && 'px-2')}>
        <WorkspaceSelector collapsed={collapsed} />
      </div>

      {/* Main Navigation */}
      <nav className={cn('flex-1 px-3 space-y-1', collapsed && 'px-2')}>
        {mainNavItems.map((item) => (
          <SidebarNavItem
            key={item.href}
            href={item.href}
            icon={item.icon}
            label={item.label}
            badge={item.badge}
            badgeVariant={item.badgeVariant}
            collapsed={collapsed}
          />
        ))}
      </nav>

      {/* Bottom Section */}
      <div className={cn('px-3 pb-4', collapsed && 'px-2')}>
        <Separator className="my-3" />

        {/* Settings */}
        <SidebarNavItem
          href="/settings"
          icon={<Settings className="w-5 h-5" />}
          label="Settings"
          collapsed={collapsed}
        />

        {/* Upgrade CTA */}
        <div className="mt-3">
          <UpgradeCTA collapsed={collapsed} />
        </div>

      </div>

      {/* Floating Collapse Toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className={cn(
          'absolute -right-3 top-5 z-10',
          'w-6 h-6 rounded-full bg-[var(--surface-hover)] border border-[var(--border-default)] shadow-sm',
          'flex items-center justify-center',
          'text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:border-[var(--border-default)] transition-colors'
        )}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? (
          <ChevronRight className="w-3.5 h-3.5" />
        ) : (
          <ChevronLeft className="w-3.5 h-3.5" />
        )}
      </button>
    </aside>
  );
}
